import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, ImagePlaceholder } from "@/components/site/SiteShell";
import { PageHero, Section, CTA, Breadcrumbs, Prose, FAQGrid } from "@/components/site/Sections";

const TITLE = "Om Elektriker-Elektriker | Autoriseret elinstallatør i København";
const DESCRIPTION =
  "Mød Elektriker-Elektriker, autoriseret elinstallatør med base på Østerbro. Læs om vores historie, værdier, certificeringer og vores team i hele Storkøbenhavn.";

export const Route = createFileRoute("/om-os")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/om-os" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/om-os" }],
  }),
  component: () => (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Om os" }]} />
      <PageHero
        variant="centered"
        eyebrow="Om os · CVR 35209468"
        title="Autoriseret elektriker med hjerte for håndværket"
        intro="Vi er en lokal elinstallatørvirksomhed med base på Østersøgade 106A på Østerbro. Vi løser alt fra mindre boligopgaver til store erhvervsentrepriser, altid med autorisation, fast pris og garanti."
        imageLabel="Vores team i København"
      />

      <Section>
        <Prose>
          <h2>Vores historie</h2>
          <p>
            Elektriker-Elektriker er drevet af elektrikere, ikke af salgsfolk. Vi har samlet
            erfaringer fra både store entrepriser og daglig service hos private i én virksomhed,
            hvor det er nemt at være kunde, og hvor svenden, der står ved din eltavle, også har
            mandat til at træffe beslutninger på stedet.
          </p>
          <p>
            Virksomheden blev startet med et enkelt princip: vi vil drive den slags
            elektrikerforretning, vi selv ville være kunde hos. Det betyder gennemskuelige priser,
            ærlig rådgivning, ordentlig dokumentation, og at vi siger fra, hvis vi vurderer at
            opgaven kan løses billigere eller bedre på en anden måde end den, kunden først havde
            tænkt.
          </p>

          <h2>Vores værdier</h2>
          <h3>Godt håndværk</h3>
          <p>
            Vi tror ikke på shortcuts. Alle vores svende er udlært, autorisationen ligger hos os
            (ikke hos en underleverandør), og hver opgave dokumenteres med KLS-rapport. Du får
            garanti på arbejdet, og TEKNIQ Garanti dækker op til 150.000 kr. ved fejl og
            mangler.
          </p>
          <h3>Gennemskuelige priser</h3>
          <p>
            Vi har én prisliste, den ligger åbent på siden. Vi giver fast pris på planlagte
            opgaver og oplyser altid prisen undervejs ved fejlfinding. Ingen små bogstaver, ingen
            "overraskelser" på fakturaen.
          </p>
          <h3>Grønt aftryk</h3>
          <p>
            Vores servicebiler er elbiler. Vi sorterer alt materiel. Og vi tilbyder en hel række
            af kerneydelser, varmepumper, ladestandere, solceller og energioptimering, der er
            med til at sænke vores kunders CO2-aftryk meget mere, end vores egen drift nogensinde
            kunne.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <ImagePlaceholder label="Foto fra værkstedet" ratio="aspect-[5/4]" />
          <div>
            <h2 className="text-3xl">Certificeringer & medlemskaber</h2>
            <ul className="mt-5 grid grid-cols-1 gap-3 text-sm text-[var(--ink)]/85 sm:grid-cols-2">
              {[
                "Autoriseret elinstallatør",
                "CVR 35209468",
                "Gazelle-virksomhed 2017 (Børsen)",
                "Højeste kreditværdighed (AAA)",
                "Medlem af TEKNIQ Arbejdsgiverne",
                "TEKNIQ Garanti op til 150.000 kr.",
                "100% elbil-flåde",
                "Garanti på arbejdet",
              ].map((b) => (
                <li key={b} className="flex gap-2"><span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{b}</li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/careers" className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">Karriere hos os</Link>
              <Link to="/kontakt" className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">Kontakt</Link>
            </div>
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">I tal</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { v: "5.000+", l: "Opgaver løst" },
            { v: "24/7", l: "Døgnvagt" },
            { v: "Garanti", l: "På alt arbejde" },
            { v: "AAA", l: "Kreditværdighed" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-border bg-card p-5">
              <div className="text-3xl font-bold text-[var(--ink)]">{s.v}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål</h2>
        <div className="mt-8">
          <FAQGrid items={[
            { q: "Hvor sidder I?", a: "Vi har hovedkontor på Østersøgade 106A, 2100 København Ø, men vi har biler ude i hele Storkøbenhavn hver eneste dag." },
            { q: "Hvor stort et område dækker I?", a: "Hele Storkøbenhavn og Nordsjælland, fra Indre By til Vedbæk, og fra Frederiksberg til Amager. Se vores liste over byer under sitemap." },
            { q: "Hvad er TEKNIQ Garanti?", a: "TEKNIQ Garanti er en garantiordning, der dækker dig som kunde, hvis vi mod forventning ikke kan udbedre en fejl. Det er et ekstra sikkerhedsnet oven på vores egen garanti." },
            { q: "Er I dyrere fordi I er autoriserede?", a: "Nej. Pris pr. time er ofte den samme som hos ikke-autoriserede, men du får dokumentation, KLS-rapport og garanti uden ekstra omkostning. Det ville være den dyre løsning at vælge en ikke-autoriseret." },
          ]} />
        </div>
      </Section>

      <CTA />
      <div className="h-10" />
    </SiteShell>
  ),
});
